import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from '../../global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  createAccount as createAccountAPI,
  deleteAccount as deleteAccountAPI,
  getAccount,
  getAccounts,
  updateAccount as updateAccountAPI,
} from './companyAccounts.api';
import { CompanyAccount } from './companyAccounts.types';

const name = 'companyAccounts';

const accountsEntityAdapter = createEntityAdapter<AsyncEntity<CompanyAccount>>({
  selectId: account => account.id,
});

export const accountsSelectors = accountsEntityAdapter.getSelectors();

export const fetchAccounts = createAsyncThunkFromFetch(`${name}/fetchAccounts`, getAccounts);
export const deleteAccount = createAsyncThunkFromFetch(`${name}/deleteAccount`, deleteAccountAPI);
export const createAccount = createAsyncThunkFromFetch(`${name}/createAccount`, createAccountAPI);
export const fetchAccount = createAsyncThunkFromFetch(`${name}/fetchAccount`, getAccount);
export const updateAccount = createAsyncThunkFromFetch(`${name}/updateAccount`, updateAccountAPI);

const accountsSlice = createSlice({
  name,
  initialState: accountsEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchAccounts.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchAccounts.fulfilled, (state, { payload }) => {
      accountsEntityAdapter.setAll(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchAccounts.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteAccount.pending, (state, { meta }) => {
      accountsEntityAdapter.updateOne(state, {
        id: meta.arg.accountId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteAccount.fulfilled, (state, { meta }) => {
      accountsEntityAdapter.removeOne(state, meta.arg.accountId);
    });
    builder.addCase(deleteAccount.rejected, (state, { meta }) => {
      accountsEntityAdapter.updateOne(state, {
        id: meta.arg.accountId,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createAccount.fulfilled, (state, { payload }) => {
      accountsEntityAdapter.addOne(state, payload);
    });
    builder.addCase(fetchAccount.fulfilled, (state, { payload }) => {
      accountsEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateAccount.fulfilled, (state, { payload, meta }) => {
      const { accountId: id } = meta.arg;
      accountsEntityAdapter.updateOne(state, { id, changes: payload });
    });
  },
});

const { reducer } = accountsSlice;

export default reducer;
